import type { JSX } from 'solid-js';
import { Show } from 'solid-js';
import { ApprovalsList } from '../components/ApprovalsList';
import { ApprovedPrsList } from '../components/ApprovedPrsList';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { Pager } from '../components/Pager';
import { Panel } from '../components/Panel';
import { QueryBoundary } from '../components/QueryBoundary';
import { Skeleton } from '../components/Skeleton';
import { t } from '../i18n';
import { useApprovalQueue } from '../lib/approval-queue';
import { useApprovedPrs } from '../lib/approved-prs';
import { useOverviewCounts } from '../lib/overview-counts';
import { pendingSummary } from '../lib/overview-tiles';
import { createPagination } from '../lib/paginate';
import { SESSION_TOKEN } from '../lib/session';

// Pending actions (DECIDE group): every PR across the account parked on
// `ask_before_acting`, longest-waiting first, with a per-row APPROVE / SKIP.
// Container — the rows are the live `/v1/approvals` read Home's queue renders
// too (`lib/approval-queue.ts`): the `approvals` topic re-reads it, so a decision
// taken elsewhere leaves this list without a reload.
//
// NO MAIN ACTION. The canvas draws "Approve all in category"; no endpoint takes
// a bulk decision, and a client loop of N merges would be a new merge path the
// server never agreed to. Each row calls only `POST /v1/prs/:id/{approve,skip}-
// pending` (`prDecideCall`), and `guardedMerge` stays the one merge PUT behind it.
//
// A CUT-SHORT HEAD SAYS SO (plan 101 slice 17 row 17-14). `/v1/approvals` is a
// bounded head with no cursor; `pending_approvals` (`/v1/overview/counts`, the
// rail badge's read) is the queue's full size. When the count exceeds what this
// list holds, the footer names both, rather than a queue of 70 reading as 50.
//
// BESIDE IT, THE APPROVED LIST. The queue above holds the PRs waiting for a
// human to say yes; this one holds the PRs a human ALREADY said yes to and
// that are still open — the ones the merge gate is now waiting on. They are
// different sets (`merge_decision = 'ask'` against `= 'auto'`), so they are
// different sections, and neither replaces the other.

export function Pending(props: { token?: string | null } = {}): JSX.Element {
  // Default to the dev session ONLY when the prop is omitted — an explicit
  // `null` is a signed-out caller (`??` would wrongly replace it).
  const token = props.token === undefined ? SESSION_TOKEN : props.token;
  // Rows, decision and re-read: the one queue Home renders too (lib/approval-queue.ts).
  const { approvals, rows, decision, decide, mayDecide } = useApprovalQueue(token);
  // The endpoint is a bounded head (`lib/overview.ts`); paging walks what it holds.
  const page = createPagination(rows, 20);
  const counts = useOverviewCounts(token);
  // The approved open PRs — the list that outlives the queue above.
  const approved = useApprovedPrs(token);
  /** The queue's full size when it is larger than the rows held, else null. */
  const beyondHead = (): number | null => {
    const total = counts.data?.pending_approvals;
    return total !== undefined && total > rows().length ? total : null;
  };

  return (
    <>
      <PageHeader title={t('pending.title')} desc={t('pending.subtitle')} />
      <Show when={token !== null} fallback={<EmptyState message={t('home.no_session')} />}>
        <Show when={decision.error()}>
          {(message) => (
            <div class="alert alert--destructive" role="alert">
              <p class="alert__title">{message()}</p>
            </div>
          )}
        </Show>
        <Panel
          title={t('pending.queue_heading')}
          action={
            <Show when={pendingSummary(rows())}>
              {(line) => <span class="pending-actions__summary">{line()}</span>}
            </Show>
          }
        >
          <QueryBoundary
            query={approvals}
            // The rows on screen decide emptiness: a decided row hidden while a
            // re-read still lists it does not count as waiting.
            isEmpty={() => rows().length === 0}
            loading={<Skeleton height="12rem" />}
            empty={<p class="list-empty">{t('approvals.empty')}</p>}
          >
            {() => (
              <>
                <ApprovalsList
                  rows={page.slice()}
                  busy={decision.pending()}
                  mayDecide={mayDecide()}
                  onDecide={decide}
                />
                <Pager pagination={page} label={t('pending.queue_heading')} />
                <Show when={beyondHead()}>
                  {(total) => (
                    <p class="list-empty" role="note">
                      {t('pending.truncated', { shown: rows().length, total: total() })}
                    </p>
                  )}
                </Show>
              </>
            )}
          </QueryBoundary>
        </Panel>

        <Panel title={t('approved_prs.heading')}>
          <QueryBoundary
            query={approved}
            isEmpty={(rows) => rows.length === 0}
            loading={<Skeleton height="10rem" />}
            empty={<p class="list-empty">{t('approved_prs.empty')}</p>}
          >
            {(rows) => <ApprovedPrsList rows={rows} />}
          </QueryBoundary>
        </Panel>
      </Show>
    </>
  );
}
